import React, { ReactElement, CSSProperties, useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styles from './layout/xform/form.module.scss';

interface Props {
  label?: string;
  dropdown: () => ReactElement;
  preview: (toggle: boolean) => ReactElement;
  toggle: boolean;
  onToggleChanged: (toggle: boolean) => void;
  dropdownHeight?: number;
  style?: React.CSSProperties;
  previewToLeft?: boolean;
}

function detectPosition(height: number | undefined, ref: React.RefObject<HTMLDivElement>): 'bottom' | 'top' {
  if (height && ref.current && window.innerHeight - ref.current.getBoundingClientRect().bottom < height) {
    return 'top';
  } else {
    return 'bottom';
  }
}

export function DropdownBaseComponent(props: Props) {
  const wrappedRef = useRef<HTMLDivElement>(null);
  const previewRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<CSSProperties>({});

  useEffect(() => {
    const onDocumentClicked = (e: MouseEvent) => {
      if (
        wrappedRef.current &&
        !wrappedRef.current.contains(e.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        props.onToggleChanged(false);
      }
    };

    document.addEventListener('click', onDocumentClicked);
    return () => document.removeEventListener('click', onDocumentClicked);
  });

  useEffect(() => {
    const updatePosition = () => {
      if (previewRef.current && props.toggle) {
        const rect = previewRef.current.getBoundingClientRect();
        const position = detectPosition(props.dropdownHeight, previewRef);
        const dropdownWidth = 500;

        setDropdownStyle({
          position: 'fixed',
          zIndex: 10000,
          left: props.previewToLeft ? rect.right - dropdownWidth : rect.left,
          top: position === 'bottom' ? rect.bottom + 10 : undefined,
          bottom: position === 'top' ? window.innerHeight - rect.top + 10 : undefined,
        });
      }
    };

    updatePosition();

    const onScroll = () => {
      updatePosition();
    };

    const onResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onResize);

    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onResize);
    };
  }, [props.toggle, props.dropdownHeight, props.previewToLeft]);

  const renderDropdown = () => {
    if (!props.toggle) return null;

    // Try to find Polaris modal backdrop or use document.body
    const portalTarget = document.querySelector('[data-portal-id]') || document.body;

    return createPortal(
      <div
        ref={dropdownRef}
        style={dropdownStyle}
        onMouseDown={e => e.stopPropagation()}
        onClick={e => e.stopPropagation()}
        data-polaris-layer
        data-polaris-overlay
      >
        <div
          style={{
            backgroundColor: '#fff',
            boxShadow: '0px 15px 125px rgba(0,0,0,0.22)',
            borderRadius: '3px',
          }}
        >
          {props.dropdown()}
        </div>
      </div>,
      portalTarget,
    );
  };

  return (
    <div className={props.label ? styles.form_group : ''} style={{ width: '100%', flexGrow: 1, ...props.style }}>
      {props.label && <label className={styles.label}>{props.label}</label>}
      <div style={{ position: 'relative', width: '100%', height: '100%' }} ref={wrappedRef}>
        <div ref={previewRef} onClick={() => props.onToggleChanged(true)} style={{ width: '100%', height: '100%' }}>
          {props.preview(props.toggle)}
        </div>
        {renderDropdown()}
      </div>
    </div>
  );
}
